import Dialog from "components/Dialog";
import Tr from "translations/Translations";
import React, {useEffect, useMemo, useState} from "react";
import SearchBar from "./AuditTaskUserSearchBar";
import Loading from "components/Loading";
import SimpleTable from "components/complex/simpleTable/SimpleTable";
import Pagination from "components/Pagination";
import useSearch from "hooks/useSearch";
import {Checkbox, withStyles} from "@material-ui/core";
import {connect} from "react-redux";
import AuditTaskElementsService from "../service/AuditTaskElementsService";

const style = theme => ({
    root: {
        padding: theme.spacing(2)
    },
    userColumn: {
        width: 250,
        wordBreak: 'break-all'
    },
    emailColumn: {
        width: 350,
        wordBreak: 'break-all'
    },
    checkbox: {
        width: 30
    },
    gridNew: {
        padding: theme.spacing(0.5)
    },
    icon: {
        marginRight: theme.spacing(1)
    },
    buttonNew: {
        marginRight: theme.spacing(2)
    },
    cell: {
        padding: theme.spacing(1),
    }
})

function AuditTaskUserPopup({
    taskId,
    excludeUserIds,
    open,
    onConfirm,
    onCancel,
    title = 'user_list',
    auditUserList = {},
    auditTaskOrganizations = {list: []},
    classes
}) {

    const { items } = auditUserList;
    const organizations = auditTaskOrganizations.list.map(o => ({value: o.orgId, label: o.name}));
    organizations.unshift({value: '', label: ''});

    const [loading, setLoading] = useState(false);
    const [chosenUsers, setChosenUsers] = useState([]);
    const [searchState, setSearchState] = useState(0);
    const searchInitValues = {name: '', email: '', organization: ''}
    const sortInitValues = {column: 'identity', direction: 'desc'}

    const {searchData, totalCount, setTotalCount, setPaginationConfig, setSearchConfig, setSortConfig,}
        = useSearch({
        search: {
            filters: {...searchInitValues}
        },
        sort: {...sortInitValues}
    });

    const setFilters = (filters) => {
        const newFilters = {...searchData.search.filters, ...filters};
        setSearchConfig({...searchData.search, filters: newFilters});
    }

    useEffect(() => {
        return () => {
            AuditTaskElementsService.clearUsers();
        }
    }, []);

    useEffect(() => {
        fetchUsers()
        // eslint-disable-next-line
    }, [searchState])

    const fetchUsers = () => {
        setLoading(true);
        const {search, pagination, sort} = searchData;
        const {page, pageSize} = pagination;
        const promises = []
        promises.push(AuditTaskElementsService.getUsers(taskId, excludeUserIds, page, pageSize, {
            ...search.filters,
            ...search.params,
        }, sort));
        if (!auditTaskOrganizations.list.length)
            promises.push(AuditTaskElementsService.getOrganizations(taskId));
        Promise.all(promises).then(promisesValues => {
            setTotalCount(promisesValues[0].totalCount);
            setLoading(false)
        });
    }

    const doSearch = () => {
        setChosenUsers([]);
        setSearchState(searchState + 1);
    }

    const handleSortChange = (id) => {
        let direction = 'desc';
        const active = searchData.sort.column === id;
        if (active) {
            direction = searchData.sort.direction === 'asc' ? 'desc' : 'asc';
        }

        setSortConfig({
            column: id,
            direction
        })
        doSearch();
    }

    const getSortProps = (id) => {
        return {
            active: searchData.sort.column === id,
            direction: searchData.sort.direction,
            onClick: () => {
                handleSortChange(id)
            }
        }
    }

    const getHeaders = () => {
        if (!items) return;
        const headers = [];
        headers.push('');
        headers.push({
            value: Tr('firstName'),
            sort: getSortProps('firstName')
        });
        headers.push({
            value: Tr('lastName'),
            sort: getSortProps('lastName')
        });
        headers.push({
            value: Tr('email'),
            sort: getSortProps('email')
        });
        headers.push({
            value: Tr('organizational_unit'),
            sort: getSortProps('organization')
        });
        return headers;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const headers = useMemo(getHeaders, [items]);

    const getHeaderClasses = () => {
        if (!items) return;
        const headers = [];
        headers.push(classes.checkbox);
        headers.push(classes.userColumn);
        headers.push(classes.userColumn);
        headers.push(classes.emailColumn);
        headers.push(undefined);
        return headers;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const headerClasses = useMemo(getHeaderClasses, [items]);

    const checkUser = (identity) => {
        if (chosenUsers.some(x => x.identity === Number(identity)))
            chosenUsers.splice(chosenUsers.indexOf(chosenUsers.filter(i => i.identity === Number(identity))[0]), 1);
        else
            chosenUsers.push(items.filter(i => i.identity === Number(identity))[0]);
        setChosenUsers([...chosenUsers]);
    }

    const getRows = () => {
        console.log('drawing rows...');
        if (!items) return;
        const rows = items.map((item) => {
            const row = [];
            row.push(<Checkbox
                edge="start"
                tabIndex={-1}
                onChange={(e) => checkUser(e.target.value)}
                value={item.identity}
                checked={chosenUsers.some(x => x.identity === item.identity)}
            />);
            row.push(item.firstName);
            row.push(item.lastName);
            row.push(item.email);
            row.push(item.organization);
            return row;
        });
        return rows;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const rows = useMemo(getRows, [items, chosenUsers]);

    const handleSearch = () => {
        doSearch();
    }

    const handleReset = () => {
        setFilters({...searchInitValues});
        setSortConfig({...sortInitValues});
        handleSearch();
    }

    const handlePaginationChange = (data) => {
        setPaginationConfig(data);
        doSearch();
    }

    const pickUsers = () => {
        onConfirm(chosenUsers);
    }

    return (
        <Dialog title={Tr(title)} open={open} maxWidth={'lg'} onClose={onCancel}>
            {!items && <Loading/>}
            {!!items && <div className={classes.root}>
                <SearchBar
                    organizations={organizations}
                    pickUsers={() => pickUsers()}
                    setFilters={setFilters}
                    filters={searchData.search.filters}
                    onSubmit={handleSearch}
                    onReset={handleReset}
                    searching={loading}
                />
                <SimpleTable
                    headers={headers}
                    items={rows}
                    columnClasses={headerClasses}
                    cellClassName={classes.cell}
                />
            </div>}

            <Pagination config={searchData.pagination} setConfig={handlePaginationChange} totalCount={totalCount}/>
        </Dialog>
    )

}

export default connect((state) => ({
    auditUserList: state.resources.auditUserListPopup,
    auditTaskOrganizations: state.resources.auditTaskOrganizations
}), {})(withStyles(style)(AuditTaskUserPopup));